import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFontSize, FontSize } from '../hooks/useFontSize';
import '../styles/pages/guestCollectionView.css';
import '../styles/header.css';
import '../styles/components/unifiedHeader.css';
import '../styles/pages/auctionDetailPage.css';
import API_BASE_URL from '../config';
import AuctionSetupModal, { AuctionEditData } from '../components/AuctionSetupModal';

interface MyAuction {
  id: number;
  item_id: number;
  item_name: string;
  item_description: string | null;
  image_id: number | null;
  status: string;
  outcome: string;
  starts_at: string;
  ends_at: string;
  minimum_bid: number;
  reserve_price: number | null;
  reserve_visible: boolean;
  buy_now_price: number | null;
  quantity: number;
  collection_window_days: number;
  collection_notes: string | null;
  auction_group: string | null;
  bid_count: number;
  winner_user_id: number | null;
  winning_bid: number | null;
}

interface Ranking {
  rank: number;
  user_id: number;
  bidder_name: string;
  amount: number;
  comment: string | null;
  result: string;
  placed_at: string;
  collection_deadline: string | null;
}

const centsToDisplay = (cents: number) => (cents / 100).toFixed(2);

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTimeRemaining = (endsAt: string): string => {
  const now = new Date();
  const end = new Date(endsAt);
  const diff = end.getTime() - now.getTime();
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const statusLabel = (status: string, outcome: string): string => {
  if (status === 'draft') return 'Draft';
  if (status === 'scheduled') return 'Scheduled';
  if (status === 'active') return 'Active';
  if (status === 'closed') {
    if (outcome === 'sold') return 'Sold';
    if (outcome === 'unsold') return 'Unsold';
    return 'Closed';
  }
  if (status === 'completed') return outcome === 'sold' ? 'Completed' : 'Unsold';
  if (status === 'cancelled') return 'Cancelled';
  return status;
};

const statusColor = (status: string): string => {
  switch (status) {
    case 'active': return '#059669';
    case 'scheduled': return '#2563eb';
    case 'closed': return '#6b7280';
    case 'completed': return '#374151';
    case 'cancelled': return '#dc2626';
    default: return '#9ca3af';
  }
};

const MyAuctionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { fontSize, setFontSize } = useFontSize();
  const token = localStorage.getItem('auth_token');

  const [auctions, setAuctions] = useState<MyAuction[]>([]);
  const [loading, setLoading] = useState(true);

  // Rankings panel
  const [selectedAuctionId, setSelectedAuctionId] = useState<number | null>(null);
  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [rankingsLoading, setRankingsLoading] = useState(false);

  // Actions
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Edit modal
  const [editAuction, setEditAuction] = useState<MyAuction | null>(null);

  // Guest theme — default to system preference, not the logged-in user's stored theme
  const [guestTheme, setGuestTheme] = useState<'light' | 'dark'>(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const savedThemePrefRef = React.useRef<string | null>(null);
  useEffect(() => {
    savedThemePrefRef.current = localStorage.getItem('theme-preference');
    localStorage.setItem('theme-preference', 'system');
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', systemTheme);
    return () => {
      if (savedThemePrefRef.current !== null) {
        localStorage.setItem('theme-preference', savedThemePrefRef.current);
      } else {
        localStorage.removeItem('theme-preference');
      }
      document.documentElement.setAttribute('data-theme',
        savedThemePrefRef.current === 'dark' ? 'dark'
          : savedThemePrefRef.current === 'light' ? 'light'
          : savedThemePrefRef.current === 'pastel' ? 'pastel'
          : window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      );
    };
  }, []);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', guestTheme);
  }, [guestTheme]);

  const fetchAuctions = useCallback(async () => {
    if (!token) { navigate('/', { replace: true }); return; }
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/auction-items/mine`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch auctions');
      const data = await response.json();
      setAuctions(data || []);
    } catch (err) {
      console.error('Error fetching my auctions:', err);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchAuctions();
  }, [fetchAuctions]);

  const fetchRankings = useCallback(async (auctionId: number) => {
    if (!token) return;
    setRankingsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auction-items/${auctionId}/rankings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch rankings');
      const data = await response.json();
      setRankings(data.rankings || []);
    } catch (err) {
      console.error('Error fetching rankings:', err);
      setRankings([]);
    } finally {
      setRankingsLoading(false);
    }
  }, [token]);

  const handleSelectAuction = (auction: MyAuction) => {
    if (selectedAuctionId === auction.id) {
      setSelectedAuctionId(null);
      setRankings([]);
      return;
    }
    setSelectedAuctionId(auction.id);
    if (auction.status === 'closed' || auction.status === 'completed') {
      fetchRankings(auction.id);
    } else {
      setRankings([]);
    }
  };

  const handleCloseAuction = async (auctionId: number) => {
    if (!token) return;
    if (!window.confirm('Close this auction early? Bids will be ranked and a winner selected if reserve is met.')) return;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auction-items/${auctionId}/close`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json();
        setActionMessage({ type: 'error', text: data.error || 'Failed to close auction' });
        return;
      }
      setActionMessage({ type: 'success', text: 'Auction closed.' });
      fetchAuctions();
      fetchRankings(auctionId);
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to close auction.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = async (auctionId: number) => {
    if (!token) return;
    if (!window.confirm('Withdraw this auction? It will be cancelled and no bids will be accepted.')) return;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auction-items/${auctionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json();
        setActionMessage({ type: 'error', text: data.error || 'Failed to withdraw auction' });
        return;
      }
      setActionMessage({ type: 'success', text: 'Auction withdrawn.' });
      setSelectedAuctionId(null);
      fetchAuctions();
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to withdraw auction.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCollect = async (auctionId: number) => {
    if (!token) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auction-items/${auctionId}/collect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json();
        setActionMessage({ type: 'error', text: data.error || 'Failed to mark collected' });
        return;
      }
      setActionMessage({ type: 'success', text: 'Item marked as collected! Auction completed.' });
      fetchAuctions();
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to mark collected.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleForfeit = async (auctionId: number) => {
    if (!token) return;
    if (!window.confirm('Mark the current winner as forfeited? The next runner-up will be promoted.')) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auction-items/${auctionId}/forfeit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json();
        setActionMessage({ type: 'error', text: data.error || 'Failed to forfeit' });
        return;
      }
      setActionMessage({ type: 'success', text: 'Winner forfeited. Next runner-up promoted.' });
      fetchAuctions();
      fetchRankings(auctionId);
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to process forfeit.' });
    } finally {
      setActionLoading(false);
    }
  };

  const selectedAuction = auctions.find(a => a.id === selectedAuctionId);

  return (
    <div className="guest-collection-view">
      {/* Header */}
      <div
        ref={(el) => {
          if (el) {
            const h = el.getBoundingClientRect().height;
            document.documentElement.style.setProperty('--guest-header-h', `${Math.ceil(h)}px`);
          }
        }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, background: 'var(--color-bg-primary)', borderBottom: '1px solid var(--color-border)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 1rem', position: 'relative' }}>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer', color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <span style={{ fontSize: '14px' }}>Back</span>
          </button>
          <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div className="unified-header-title">My Auctions</div>
          </div>
          <div className="unified-header-actions">
            <button
              onClick={() => {
                const sizes: FontSize[] = ['small', 'medium', 'large', 'x-large'];
                const currentIndex = sizes.indexOf(fontSize);
                const nextIndex = (currentIndex + 1) % sizes.length;
                setFontSize(sizes[nextIndex]);
              }}
              className="unified-header-font-toggle"
              title={`Font size: ${fontSize}`}
              aria-label="Change font size"
            >
              <span className="font-toggle-text">Aa</span>
              <span className="font-toggle-bars">
                <span className={`bar bar-1 ${['small','medium','large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-2 ${['medium','large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-3 ${['large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-4 ${fontSize === 'x-large' ? 'active' : ''}`}></span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="guest-content">
        {actionMessage && (
          <div style={{
            padding: '10px 12px',
            borderRadius: 8,
            fontSize: '14px',
            marginBottom: 12,
            background: actionMessage.type === 'success' ? '#ecfdf5' : '#fef2f2',
            color: actionMessage.type === 'success' ? '#065f46' : '#991b1b',
          }}>
            {actionMessage.text}
          </div>
        )}

        {loading ? (
          <div className="marketplace-loading">Loading your auctions...</div>
        ) : auctions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--color-text-muted)' }}>
            <p>You haven't created any auctions yet.</p>
            <p style={{ fontSize: '14px', marginTop: 8 }}>Long-press an item in your inventory and tap "Auction" to get started.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {auctions.map((auction) => (
              <div key={auction.id}>
                {/* Auction card */}
                <div
                  className="auction-card"
                  onClick={() => handleSelectAuction(auction)}
                  style={{ background: selectedAuctionId === auction.id ? 'var(--color-bg-secondary)' : undefined }}
                >
                  <div className="auction-card-image">
                    {auction.image_id ? (
                      <img src={`${API_BASE_URL}/api/images/${auction.image_id}/public`} alt={auction.item_name} />
                    ) : (
                      <div className="auction-card-placeholder">
                        {auction.item_name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="auction-card-badge" style={{ background: statusColor(auction.status) }}>
                      {statusLabel(auction.status, auction.outcome)}
                    </div>
                  </div>
                  <div className="auction-card-info">
                    <div className="auction-card-name">{auction.item_name}</div>
                    <div className="auction-card-price">
                      Min ${centsToDisplay(auction.minimum_bid)}
                      {auction.winning_bid && (
                        <span style={{ color: '#059669', marginLeft: 8 }}>
                          Won ${centsToDisplay(auction.winning_bid)}
                        </span>
                      )}
                    </div>
                    <div className="auction-card-meta">
                      {auction.bid_count} {auction.bid_count === 1 ? 'bid' : 'bids'}
                      {auction.status === 'active' && ` · ${formatTimeRemaining(auction.ends_at)} left`}
                      {(auction.status === 'closed' || auction.status === 'completed') && ` · Ended ${formatDate(auction.ends_at)}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', paddingRight: 4 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2"
                      style={{ transform: selectedAuctionId === auction.id ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </div>

                {/* Expanded detail panel */}
                {selectedAuctionId === auction.id && (
                  <div style={{ padding: '12px 16px', background: 'var(--color-bg-secondary)', borderBottom: '1px solid var(--color-border)' }}>
                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                      {(auction.status === 'draft' || auction.status === 'scheduled' || auction.status === 'active') && (
                        <button
                          onClick={() => setEditAuction(auction)}
                          style={actionBtnStyle('#2563eb')}
                        >
                          Edit
                        </button>
                      )}
                      {auction.status === 'active' && (
                        <>
                          <button
                            onClick={() => handleCloseAuction(auction.id)}
                            disabled={actionLoading}
                            style={actionBtnStyle('#6b7280')}
                          >
                            Close Early
                          </button>
                          <button
                            onClick={() => handleWithdraw(auction.id)}
                            disabled={actionLoading}
                            style={actionBtnStyle('#dc2626')}
                          >
                            Withdraw
                          </button>
                        </>
                      )}
                      {auction.status === 'scheduled' && (
                        <button
                          onClick={() => handleWithdraw(auction.id)}
                          disabled={actionLoading}
                          style={actionBtnStyle('#dc2626')}
                        >
                          Cancel
                        </button>
                      )}
                      {auction.status === 'closed' && auction.outcome === 'sold' && (
                        <>
                          <button
                            onClick={() => handleCollect(auction.id)}
                            disabled={actionLoading}
                            style={actionBtnStyle('#059669')}
                          >
                            Mark Collected
                          </button>
                          <button
                            onClick={() => handleForfeit(auction.id)}
                            disabled={actionLoading}
                            style={actionBtnStyle('#d97706')}
                          >
                            Winner Forfeit
                          </button>
                        </>
                      )}
                      {auction.status === 'draft' && (
                        <button
                          onClick={async () => {
                            if (!token) return;
                            setActionLoading(true);
                            try {
                              await fetch(`${API_BASE_URL}/api/auction-items/${auction.id}/publish`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` },
                              });
                              setActionMessage({ type: 'success', text: 'Auction published!' });
                              fetchAuctions();
                            } catch {
                              setActionMessage({ type: 'error', text: 'Failed to publish.' });
                            } finally {
                              setActionLoading(false);
                            }
                          }}
                          disabled={actionLoading}
                          style={actionBtnStyle('#2563eb')}
                        >
                          Publish
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/marketplace/auction/${auction.id}`)}
                        style={{
                          padding: '8px 14px',
                          background: 'transparent',
                          border: '1px solid var(--color-border)',
                          borderRadius: 6,
                          fontSize: '13px',
                          color: 'var(--color-text-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        View Listing
                      </button>
                    </div>

                    {/* Rankings table (closed/completed) */}
                    {(auction.status === 'closed' || auction.status === 'completed') && (
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 8 }}>
                          Bid Rankings
                        </div>
                        {rankingsLoading ? (
                          <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '8px 0' }}>Loading...</div>
                        ) : rankings.length === 0 ? (
                          <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '8px 0' }}>No bids received.</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {rankings.map((r) => (
                              <div key={r.rank} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 10,
                                padding: '8px 10px',
                                borderRadius: 8,
                                background: r.result === 'winner' ? '#ecfdf5' : r.result === 'forfeited' ? '#fef2f2' : 'var(--color-bg-primary)',
                                border: r.result === 'winner' ? '1px solid #a7f3d0' : '1px solid var(--color-border)',
                              }}>
                                <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--color-text-muted)', minWidth: 20 }}>
                                  #{r.rank}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                                    {r.bidder_name}
                                    {r.result === 'winner' && <span style={{ color: '#059669', marginLeft: 6, fontSize: '12px' }}>WINNER</span>}
                                    {r.result === 'forfeited' && <span style={{ color: '#dc2626', marginLeft: 6, fontSize: '12px' }}>FORFEITED</span>}
                                    {r.result === 'runner_up' && <span style={{ color: '#6b7280', marginLeft: 6, fontSize: '12px' }}>RUNNER-UP</span>}
                                  </div>
                                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                                    ${centsToDisplay(r.amount)}
                                  </div>
                                  {r.comment && (
                                    <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: 4, fontStyle: 'italic' }}>
                                      "{r.comment}"
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Edit Auction Modal */}
      {editAuction && token && (
        <AuctionSetupModal
          itemId={editAuction.item_id}
          itemName={editAuction.item_name}
          token={token}
          onClose={() => setEditAuction(null)}
          onCreated={() => {
            setEditAuction(null);
            setActionMessage({ type: 'success', text: 'Auction updated.' });
            fetchAuctions();
          }}
          editData={{
            id: editAuction.id,
            status: editAuction.status,
            starts_at: editAuction.starts_at,
            ends_at: editAuction.ends_at,
            minimum_bid: editAuction.minimum_bid,
            reserve_price: editAuction.reserve_price,
            reserve_visible: editAuction.reserve_visible,
            buy_now_price: editAuction.buy_now_price,
            quantity: editAuction.quantity,
            collection_window_days: editAuction.collection_window_days,
            collection_notes: editAuction.collection_notes,
            auction_group: editAuction.auction_group,
          }}
        />
      )}
    </div>
  );
};

const actionBtnStyle = (color: string): React.CSSProperties => ({
  padding: '8px 14px',
  background: color,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
});

export default MyAuctionsPage;
